import Vue from 'vue'
import { ActionTree } from 'vuex'
import * as types from './mutation-types'
import RootState from '@vue-storefront/core/types/RootState'
import CategoryState from './CategoryState'
import { quickSearchByQuery } from '@vue-storefront/core/lib/search'
import { buildFilterProductsQuery } from '@vue-storefront/core/helpers'
import { router } from '@vue-storefront/core/app'
import { localizedDispatcherRoute, storeCode as getStoreCode } from '@vue-storefront/core/lib/multistore'
import FilterVariant from '../../types/FilterVariant'
import { CategoryService } from '@vue-storefront/core/data-resolver'
import { changeFilterQuery } from '../../helpers/filterHelpers'
import config from 'config'
import { DataResolver } from 'core/data-resolver/types/DataResolver';
import { Category } from '../../types/Category';
import { _prepareCategoryPathIds } from '../../helpers/categoryHelpers';
import { prefetchStockItems } from '../../helpers/cacheProductsHelper';
import chunk from 'lodash-es/chunk'
import omit from 'lodash-es/omit'
import cloneDeep from 'lodash-es/cloneDeep'
import { parseCategoryPath } from '@vue-storefront/core/modules/breadcrumbs/helpers'
import createCategoryListQuery from '@vue-storefront/core/modules/catalog/helpers/createCategoryListQuery'
import { transformCategoryUrl } from '@vue-storefront/core/modules/url/helpers/transformUrl';

/** Same full-tree query as theme `loadMenuCategories` / Default layout megamenu prefetch */
function isMegamenuCatalogTreeQuery ({
  parent,
  key,
  value,
  level,
  onlyActive,
  onlyNotEmpty
}: {
  parent: unknown
  key: unknown
  value: unknown
  level: unknown
  onlyActive: boolean
  onlyNotEmpty: boolean
}): boolean {
  if (parent != null) return false
  if (level !== null && level !== undefined) return false
  if (key !== 'include_in_menu') return false
  if (Number(value) !== 1) return false
  if (onlyActive !== true) return false
  if (onlyNotEmpty !== false) return false
  return true
}

function normalizeFetchMenuCategoriesParent (parent: unknown): string | number | null {
  if (parent == null) return null
  if (Array.isArray(parent)) {
    return parent.map((p) => (typeof p === 'object' && p && 'id' in (p as object) ? (p as { id: unknown }).id : p)).join(',')
  }
  if (typeof parent === 'object') return (parent as { id: unknown }).id as number
  return parent as number
}

function fetchMenuCategoriesFingerprint ({
  parent,
  key,
  value,
  level,
  levelMin,
  levelMax,
  onlyActive,
  onlyNotEmpty,
  size,
  start,
  sort,
  includeFields,
  excludeFields,
  storeCode
}: {
  parent: unknown
  key: unknown
  value: unknown
  level: unknown
  levelMin: unknown
  levelMax: unknown
  onlyActive: boolean
  onlyNotEmpty: boolean
  size: number
  start: number
  sort: string
  includeFields: unknown
  excludeFields: unknown
  storeCode: string
}): string {
  const normValue = Array.isArray(value) ? [...value].map(String).sort() : value
  return JSON.stringify({
    storeCode,
    p: normalizeFetchMenuCategoriesParent(parent),
    key,
    value: normValue,
    level,
    levelMin,
    levelMax,
    onlyActive,
    onlyNotEmpty,
    size,
    start,
    sort,
    includeFields: Array.isArray(includeFields) ? [...includeFields].sort() : includeFields,
    excludeFields: Array.isArray(excludeFields) ? [...excludeFields].sort() : excludeFields
  })
}

const fetchMenuCategoriesInflight = new Map<string, Promise<unknown>>()

const actions: ActionTree<CategoryState, RootState> = {
  async loadCategoryProducts ({ commit, getters, dispatch, rootState }, { route, category, pageSize = 50 } = {}) {
    const searchCategory = category || getters.getCategoryFrom(route.path) || {}
    const areFiltersInQuery = !!Object.keys(route[config.products.routerFiltersSource]).length
    let categoryMappedFilters = getters.getFiltersMap[searchCategory.id]
    if (!categoryMappedFilters && areFiltersInQuery) { // loading all filters only when some filters are currently chosen and category has no available filters yet
      await dispatch('loadCategoryFilters', searchCategory)
      categoryMappedFilters = getters.getFiltersMap[searchCategory.id]
    }
    const searchQuery = getters.getCurrentFiltersFrom(route[config.products.routerFiltersSource], categoryMappedFilters)
    const filterQr = buildFilterProductsQuery(searchCategory, searchQuery.filters)
    const { items, perPage, start, total, aggregations, attributeMetadata } = await dispatch('product/findProducts', {
      query: filterQr,
      sort: searchQuery.sort || `${config.products.defaultSortBy.attribute}:${config.products.defaultSortBy.order}`,
      includeFields: config.entities.productList.includeFields,
      excludeFields: config.entities.productList.excludeFields,
      size: pageSize,
      configuration: searchQuery.filters,
      options: {
        populateRequestCacheTags: true,
        prefetchGroupProducts: false,
        setProductErrors: false,
        fallbackToDefaultWhenNoAvailable: true,
        assignProductConfiguration: false,
        separateSelectedVariant: false
      }
    }, { root: true })
    await dispatch('loadAvailableFiltersFrom', {
      aggregations,
      attributeMetadata,
      category: searchCategory,
      filters: searchQuery.filters
    })
    commit(types.CATEGORY_SET_SEARCH_PRODUCTS_STATS, { perPage, start, total })
    commit(types.CATEGORY_SET_PRODUCTS, items)

    return items
  },
  async loadMoreCategoryProducts ({ commit, getters, rootState, dispatch }) {
    const { perPage, start, total } = getters.getCategorySearchProductsStats
    const totalValue = typeof total === 'object' ? total.value : total
    if (start >= totalValue || totalValue < perPage) return

    const searchQuery = getters.getCurrentSearchQuery
    const filterQr = buildFilterProductsQuery(getters.getCurrentCategory, searchQuery.filters)
    const searchResult = await dispatch('product/findProducts', {
      query: filterQr,
      sort: searchQuery.sort || `${config.products.defaultSortBy.attribute}:${config.products.defaultSortBy.order}`,
      start: start + perPage,
      size: perPage,
      includeFields: config.entities.productList.includeFields,
      excludeFields: config.entities.productList.excludeFields,
      configuration: searchQuery.filters,
      options: {
        populateRequestCacheTags: true,
        prefetchGroupProducts: false,
        setProductErrors: false,
        fallbackToDefaultWhenNoAvailable: true,
        assignProductConfiguration: false,
        separateSelectedVariant: false
      }
    }, { root: true })
    commit(types.CATEGORY_SET_SEARCH_PRODUCTS_STATS, {
      perPage: searchResult.perPage,
      start: searchResult.start,
      total: searchResult.total
    })

    commit(types.CATEGORY_ADD_PRODUCTS, searchResult.items)

    return searchResult.items
  },
  async cacheProducts ({ commit, getters, dispatch, rootState }, { route } = {}) {
    if (config.api.saveBandwidthOverCache) {
      return
    }

    const searchCategory = getters.getCategoryFrom(route.path) || {}
    const searchQuery = getters.getCurrentFiltersFrom(route[config.products.routerFiltersSource])
    const filterQr = buildFilterProductsQuery(searchCategory, searchQuery.filters)

    const cachedProductsResponse = await dispatch('product/findProducts', {
      query: filterQr,
      sort: searchQuery.sort,
      options: {
        populateRequestCacheTags: false,
        prefetchGroupProducts: false
      }
    }, { root: true })
    if (config.products.filterUnavailableVariants) { // prefetch the stock items
      const skus = prefetchStockItems(cachedProductsResponse, rootState.stock.cache)

      for (const chunkItem of chunk(skus, 15)) {
        dispatch('stock/list', { skus: chunkItem }, { root: true }) // store it in the cache
      }
    }
  },
  async findCategories (context, categorySearchOptions: DataResolver.CategorySearchOptions): Promise<Category[]> {
    return CategoryService.getCategories(categorySearchOptions)
  },
  async loadCategories ({ commit, getters }, categorySearchOptions: DataResolver.CategorySearchOptions): Promise<Category[]> {
    const searchingByIds = !(!categorySearchOptions || !categorySearchOptions.filters || !categorySearchOptions.filters.id)
    const searchedIds: string[] = searchingByIds ? [...categorySearchOptions.filters.id].map(String) : []
    const loadedCategories: Category[] = []
    if (searchingByIds && !categorySearchOptions.reloadAll) { // removing from search query already loaded categories, they are added to returned results
      for (const [categoryId, category] of Object.entries(getters.getCategoriesMap)) {
        if (searchedIds.includes(categoryId)) {
          loadedCategories.push(category as Category)
        }
      }
      categorySearchOptions.filters.id = searchedIds.filter(categoryId => !getters.getCategoriesMap[categoryId] && !getters.getNotFoundCategoryIds.includes(categoryId))
    }
    if (!searchingByIds || categorySearchOptions.filters.id.length) {
      categorySearchOptions.filters = Object.assign(cloneDeep(config.entities.category.filterFields), categorySearchOptions.filters ? cloneDeep(categorySearchOptions.filters) : {})
      const categories = await CategoryService.getCategories(categorySearchOptions)
      if (Vue.prototype.$cacheTags) {
        categories.forEach(category => {
          Vue.prototype.$cacheTags.add(`C${category.id}`)
        })
      }
      const notFoundCategories = searchedIds.filter(categoryId => !categories.some(cat => cat.id === parseInt(categoryId) || cat.id === categoryId))

      commit(types.CATEGORY_ADD_CATEGORIES, categories)
      commit(types.CATEGORY_ADD_NOT_FOUND_CATEGORY_IDS, notFoundCategories)
      return [...loadedCategories, ...categories]
    }
    return loadedCategories
  },
  async loadCategory ({ commit }, categorySearchOptions: DataResolver.CategorySearchOptions): Promise<Category> {
    const categories: Category[] = await CategoryService.getCategories(categorySearchOptions)
    const category: Category = categories && categories.length ? categories[0] : null
    if (category && Vue.prototype.$cacheTags) {
      Vue.prototype.$cacheTags.add(`C${category.id}`)
    }
    if (category) {
      commit(types.CATEGORY_ADD_CATEGORY, category)
    }
    return category
  },
  /**
   * Fetch and process filters from current category and sets them in available filters.
   */
  async loadCategoryFilters ({ dispatch, getters }, category) {
    const searchCategory = category || getters.getCurrentCategory
    const filterQr = buildFilterProductsQuery(searchCategory)
    const { aggregations, attributeMetadata } = await quickSearchByQuery({
      query: filterQr,
      size: config.products.maxFiltersQuerySize,
      excludeFields: ['*']
    })
    await dispatch('loadAvailableFiltersFrom', { aggregations, attributeMetadata, category })
  },
  async loadAvailableFiltersFrom ({ commit, getters, dispatch }, { aggregations, attributeMetadata, category, filters = {} }) {
    if (config.entities.attribute.loadByAttributeMetadata) {
      await dispatch('attribute/loadCategoryAttributes', { attributeMetadata }, { root: true })
    }
    const aggregationFilters = getters.getAvailableFiltersFrom(aggregations)
    const currentCategory = category || getters.getCurrentCategory
    const categoryMappedFilters = getters.getFiltersMap[currentCategory.id]
    let resultFilters = aggregationFilters
    const filtersKeys = Object.keys(filters)
    if (categoryMappedFilters && filtersKeys.length) {
      resultFilters = Object.assign(cloneDeep(categoryMappedFilters), cloneDeep(omit(aggregationFilters, filtersKeys)))
    }
    commit(types.CATEGORY_SET_CATEGORY_FILTERS, { category, filters: resultFilters })
  },

  async switchSearchFilters ({ dispatch }, filterVariants: FilterVariant[] = []) {
    let currentQuery = router.currentRoute[config.products.routerFiltersSource]
    filterVariants.forEach(filterVariant => {
      currentQuery = changeFilterQuery({ currentQuery, filterVariant })
    })
    await dispatch('changeRouterFilterParameters', currentQuery)
  },
  async resetSearchFilters ({ dispatch }) {
    await dispatch('changeRouterFilterParameters', {})
  },
  async changeRouterFilterParameters (context, query) {
    router.push({ [config.products.routerFiltersSource]: query })
  },
  async loadCategoryBreadcrumbs ({ dispatch, getters }, { category, currentRouteName, omitCurrent = false }) {
    if (!category) return
    const categoryHierarchyIds = category.parent_ids ? [...category.parent_ids, category.id] : _prepareCategoryPathIds(category) // getters.getCategoriesHierarchyMap.find(categoryMapping => categoryMapping.includes(category.id))
    const categoryFilters = Object.assign({ id: categoryHierarchyIds }, cloneDeep(config.entities.category.breadcrumbFilterFields))
    const categories = await dispatch('loadCategories', { filters: categoryFilters, reloadAll: Object.keys(config.entities.category.breadcrumbFilterFields).length > 0 })
    const sorted = []
    for (const id of categoryHierarchyIds) {
      const index = categories.findIndex(cat => cat.id.toString() === id)
      if (index >= 0 && (!omitCurrent || categories[index].id !== category.id)) {
        sorted.push(categories[index])
      }
    }
    await dispatch('breadcrumbs/set', { current: currentRouteName, routes: parseCategoryPath(sorted) }, { root: true })
    return sorted
  },
  /**
   * Load categories within specified parent
   * @param {Object} commit promise
   * @param {Object} parent parent category
   */
  async fetchMenuCategories ({ commit, getters, dispatch, state }, {
    parent = null,
    key = null,
    value = null,
    level = null,
    levelMin = null,
    levelMax = null,
    onlyActive = true,
    onlyNotEmpty = false,
    size = 4000,
    start = 0,
    sort = 'position:asc',
    includeFields = (config.entities.optimize ? config.entities.category.includeFields : null),
    excludeFields = (config.entities.optimize ? config.entities.category.excludeFields : null),
    skipCache = false
  }) {
    const { searchQuery, isCustomizedQuery } = createCategoryListQuery({ parent, level, levelMin, levelMax, key, value, onlyActive, onlyNotEmpty })
    const megamenuCatalog = isMegamenuCatalogTreeQuery({ parent, key, value, level, onlyActive, onlyNotEmpty })
    const activeStoreCode = getStoreCode()
    const fingerprint = fetchMenuCategoriesFingerprint({
      parent,
      key,
      value,
      level,
      levelMin,
      levelMax,
      onlyActive,
      onlyNotEmpty,
      size,
      start,
      sort,
      includeFields,
      excludeFields,
      storeCode: activeStoreCode
    })
    const inflightKey = `${skipCache ? '1' : '0'}:${fingerprint}`
    const megamenuCatalogAlreadySatisfied =
      megamenuCatalog &&
      !skipCache &&
      state.menuIncludeInMenuCatalogRequestKey === fingerprint

    const shouldLoadCategories = skipCache || (isCustomizedQuery && !megamenuCatalogAlreadySatisfied)

    if (!shouldLoadCategories) {
      return { items: getters.getMenuCategories, total: getters.getMenuCategories.length }
    }

    const existing = fetchMenuCategoriesInflight.get(inflightKey)
    if (existing) {
      return existing
    }

    const work = (async () => {
      try {
        const resp = await quickSearchByQuery({ entityType: 'category', query: searchQuery, sort, size, start, includeFields, excludeFields })

        // Commit menu data before URL mapping so a slow mapping pass cannot
        // leave the megamenu with only incidental (homepage) children.
        if (megamenuCatalog) {
          commit(types.CATEGORY_SET_MENU_CATEGORIES, { items: resp.items })
          commit(types.CATEGORY_SET_MENU_INCLUDE_IN_MENU_CATALOG_REQUEST_KEY, fingerprint)
        } else {
          commit(types.CATEGORY_UPD_MENU_CATEGORIES, { items: resp.items })
        }

        if (parent != null) {
          const parentIds = Array.isArray(parent)
            ? parent.map((p) => (typeof p === 'object' && p && 'id' in (p as object) ? (p as { id: unknown }).id : p))
            : [typeof parent === 'object' && parent && 'id' in (parent as object) ? (parent as { id: unknown }).id : parent]
          commit(types.CATEGORY_ADD_MENU_CHILDREN_LOADED_PARENTS, parentIds)
        }

        await dispatch('registerCategoryMapping', { categories: resp.items })

        return resp
      } finally {
        fetchMenuCategoriesInflight.delete(inflightKey)
      }
    })()

    fetchMenuCategoriesInflight.set(inflightKey, work)
    return work
  },
  async registerCategoryMapping ({ dispatch }, { categories }) {
    for (const category of categories) {
      if (!category.url_path) continue
      const url = localizedDispatcherRoute(category.url_path)

      if (category.product_count === 1) {
        await dispatch('url/removeMapping', { url }, { root: true })
        continue
      }
      if (typeof category.product_count !== 'number') {
        continue
      }
      await dispatch('url/registerMapping', {
        url,
        routeData: transformCategoryUrl(category)
      }, { root: true })
    }
  }
  /** Below actions are not used from 1.12 and can be removed to reduce bundle */
  // ...require('./deprecatedActions').default
}

export default actions
